import { useEffect, useRef } from 'react';
import type { State } from 'xstate';
import { Logger } from '@/idv/utils';

// A stuck or abandoned flow is diagnosed from the order of states it passed through, not from
// any single transition.
const MAX_PATH_STEPS = 40;
const useLogStateMachine = (name: string, state: any) => {
  const done = Boolean((state as State<any, any>).done);
  // xstate returns a new State object per transition and nested state values are objects, so
  // neither compares by identity. Keying the effect off either would fire it on every render.
  const step = typeof state.value === 'string' ? state.value : JSON.stringify(state.value);
  const path = useRef<string[]>([]);
  const hasFlushed = useRef(false);
  // Held in a ref because the unmount cleanup below has empty deps: a closure would report the
  // state from the first render.
  const flush = useRef<() => void>(() => undefined);
  flush.current = () => {
    if (hasFlushed.current) {
      return;
    }
    hasFlushed.current = true;
    Logger.track(`${done ? '(done)' : '(abandoned)'} ${step}`, {
      name,
      value: step,
      done,
      path: path.current.join(' > '),
      steps: path.current.length
    });
  };
  useEffect(() => {
    if (path.current[path.current.length - 1] !== step && path.current.length < MAX_PATH_STEPS) {
      path.current.push(step);
    }
  }, [step]);
  useEffect(() => {
    if (done) {
      flush.current();
    }
  }, [done]);
  useEffect(() => () => flush.current(), []);
};
export default useLogStateMachine;